import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type {
  FindingAction,
  FindingActionKind,
  ValidationResult,
} from "../types";
import {
  actionMap,
  effectiveStatus,
  failGateSatisfied,
  noteMap,
  withAction,
} from "./findings";

export interface PersistTarget {
  caseId: string;
  target: "summary" | "timeline" | "costs" | "volume";
  volumeId?: string;
}

// Centralises per-finding action state for a validated artefact:
//  - seeds from the stored validation.actions and resets when a fresh
//    validation arrives (regenerate → checked_at changes → actions clear),
//  - returns an effective validation (with current actions) for the badge,
//    content markers, and summary bar,
//  - computes the export/copy gate (FAIL with unaddressed findings),
//  - persists each action to the server when `persist` is provided.
export function useFindingActions(
  validation: ValidationResult | null | undefined,
  persist: PersistTarget | null,
) {
  const [actions, setActions] = useState<FindingAction[]>(
    validation?.actions ?? [],
  );
  const key = validation?.checked_at;
  useEffect(() => {
    setActions(validation?.actions ?? []);
  }, [key]);

  const v: ValidationResult | null = validation
    ? { ...validation, actions }
    : null;
  const am = v ? actionMap(v) : {};
  const notes = v ? noteMap(v) : {};
  const blocked =
    !!v && effectiveStatus(v, am) === "fail" && !failGateSatisfied(v, am);

  const pCase = persist?.caseId;
  const pTarget = persist?.target;
  const pVolume = persist?.volumeId;
  const onAction = useCallback(
    (findingId: string, kind: FindingActionKind, note?: string) => {
      setActions((prev) => {
        const next = withAction(prev, findingId, kind, note);
        if (pCase && pTarget) {
          void api
            .saveValidationActions(pCase, pTarget, next, pVolume)
            .catch(() => {});
        }
        return next;
      });
    },
    [pCase, pTarget, pVolume],
  );

  return { v, actionMap: am, noteMap: notes, onAction, blocked };
}
